import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { useOrgFilter } from "./OrgFilterContext";

const DmDrawerContext = createContext(null);

/**
 * Single bottom-right Messages panel (list + thread). Cleared on org switch / social.
 */
export function DmDrawerProvider({ children }) {
  const { selectedOrgId } = useOrgFilter();
  const [open, setOpen] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [activeConversationId, setActiveConversationId] = useState(null);

  useEffect(() => {
    setOpen(false);
    setMinimized(false);
    setActiveConversationId(null);
  }, [selectedOrgId]);

  const openPanel = useCallback(({ conversationId } = {}) => {
    if (conversationId != null) {
      const id = Number(conversationId);
      if (Number.isFinite(id)) setActiveConversationId(id);
    }
    setMinimized(false);
    setOpen(true);
  }, []);

  const closePanel = useCallback(() => {
    setOpen(false);
    setMinimized(false);
  }, []);

  const selectConversation = useCallback((conversationId) => {
    const id = Number(conversationId);
    if (!Number.isFinite(id)) return;
    setActiveConversationId(id);
    setMinimized(false);
    setOpen(true);
  }, []);

  const toggleMinimized = useCallback(() => {
    setMinimized((m) => !m);
  }, []);

  /** Alias for Members / notifications — opens panel on a conversation */
  const openConversation = useCallback(
    (conversationId, _meta = {}) => {
      openPanel({ conversationId });
    },
    [openPanel]
  );

  return (
    <DmDrawerContext.Provider
      value={{
        open,
        minimized,
        activeConversationId,
        openPanel,
        closePanel,
        selectConversation,
        toggleMinimized,
        openConversation,
        setActiveConversationId,
      }}
    >
      {children}
    </DmDrawerContext.Provider>
  );
}

export function useDmDrawer() {
  const ctx = useContext(DmDrawerContext);
  if (!ctx) {
    throw new Error("useDmDrawer must be used within DmDrawerProvider");
  }
  return ctx;
}

/** Drawer actions for components that may render in isolated previews/tests. */
export function useOptionalDmDrawer() {
  return useContext(DmDrawerContext);
}
